import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, Menu, MenuItem, Checkbox, ListItemText, Skeleton,
  CircularProgress, useMediaQuery, useTheme, ListItemIcon,
} from '@mui/material';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import SwipeIcon from '@mui/icons-material/Swipe';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { BoardColumn, TaskListItem, TaskStatus } from '../../services/taskService';
import { tokens } from '../../theme';
import { EmptyState, SectionCard } from '../../components/ui';
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER, loadPreference, savePreference } from './taskShared';
import TaskCard from './TaskCard';

const COLUMNS_PREF_KEY = 'visitio_tasks_board_columns';

const DEFAULT_VISIBLE: TaskStatus[] = [
  'TODO', 'IN_PROGRESS', 'ON_HOLD', 'IN_REVIEW', 'IN_APPROVAL', 'NEEDS_REWORK', 'DONE',
];

interface Props {
  columns: BoardColumn[];
  loading: boolean;
  onOpenTask: (id: string) => void;
  onMoveTask: (task: TaskListItem, next: TaskStatus) => void;
  onCardMenu: (event: React.MouseEvent<HTMLElement>, task: TaskListItem) => void;
  /** Only managers may drag freely; employees are limited by the workflow anyway. */
  canDrag: boolean;
  /** Pusty stan tablicy (np. z przyciskiem „Nowe zadanie”); domyslnie ogolny komunikat. */
  emptyState?: React.ReactNode;
}

const COLUMN_WIDTH = { xs: 'min(84vw, 320px)', sm: 300 };

const StatusDot: React.FC<{ status: TaskStatus }> = ({ status }) => (
  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR[status].dot, flexShrink: 0 }} />
);

export const TaskBoard: React.FC<Props> = ({
  columns, loading, onOpenTask, onMoveTask, onCardMenu, canDrag, emptyState,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [visible, setVisible] = useState<TaskStatus[]>(
    () => loadPreference<{ columns: TaskStatus[] }>(COLUMNS_PREF_KEY, { columns: DEFAULT_VISIBLE }).columns,
  );
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [dragged, setDragged] = useState<TaskListItem | null>(null);
  const [dropTarget, setDropTarget] = useState<TaskStatus | null>(null);

  const toggleColumn = (status: TaskStatus) => {
    setVisible((prev) => {
      const next = prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status];
      // Never leave the board completely empty.
      const safe = next.length === 0 ? prev : next;
      savePreference(COLUMNS_PREF_KEY, { columns: safe });
      return safe;
    });
  };

  const shownColumns = useMemo(
    () => STATUS_ORDER.filter((s) => visible.includes(s))
      .map((s) => columns.find((c) => c.status === s))
      .filter(Boolean) as BoardColumn[],
    [columns, visible],
  );

  const handleDragStart = useCallback((event: React.DragEvent, task: TaskListItem) => {
    setDragged(task);
    event.dataTransfer.effectAllowed = 'move';
    // Some browsers require data to be set for the drag to begin at all.
    event.dataTransfer.setData('text/plain', task.id);
  }, []);

  const handleDrop = (status: TaskStatus) => {
    setDropTarget(null);
    const task = dragged;
    setDragged(null);
    if (!task || task.status === status) return;
    onMoveTask(task, status);
  };

  const totalTasks = columns.reduce((sum, c) => sum + c.tasks.length, 0);

  if (loading && columns.length === 0) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5, overflow: 'hidden', pt: 6 }} aria-busy="true" aria-label="Ładowanie zadań">
        {Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ width: COLUMN_WIDTH, flexShrink: 0, bgcolor: tokens.surfaceStrong, borderRadius: '14px', p: 1.25 }}>
            <Skeleton width="45%" height={22} sx={{ mx: 0.75, mb: 1 }} />
            {Array.from({ length: 3 - (i % 2) }).map((__, j) => (
              <Skeleton key={j} variant="rounded" height={112} sx={{ mb: 1, bgcolor: tokens.paper }} />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  if (!loading && totalTasks === 0) {
    return (
      <>
        {emptyState || (
          <SectionCard>
            <EmptyState
              icon={<TaskAltOutlinedIcon />}
              title="Brak zadań do wyświetlenia"
              description="Zmień filtry albo utwórz pierwsze zadanie przyciskiem „Nowe zadanie”."
            />
          </SectionCard>
        )}
      </>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1.25, minHeight: 36 }}>
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0, color: tokens.muted }}>
          {isMobile && (
            <>
              <SwipeIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ lineHeight: 1.3 }}>Przesuń w bok, aby zobaczyć kolejne kolumny</Typography>
            </>
          )}
          {loading && <CircularProgress size={14} sx={{ ml: 0.5, flexShrink: 0 }} aria-label="Odświeżanie" />}
        </Stack>
        <Button
          size="small"
          variant="text"
          startIcon={<ViewColumnOutlinedIcon />}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ flexShrink: 0 }}
          aria-haspopup="menu"
        >
          Kolumny ({shownColumns.length})
        </Button>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {STATUS_ORDER.map((status) => (
            <MenuItem key={status} onClick={() => toggleColumn(status)} sx={{ pr: 2 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox edge="start" size="small" checked={visible.includes(status)} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <StatusDot status={status} />
              <ListItemText sx={{ ml: 1.25 }}>{STATUS_LABEL[status]}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          pb: 1.5,
          // Na telefonie kolumny dochodza do krawedzi ekranu i przewijaja sie po jednej.
          mx: { xs: -2, sm: 0 },
          px: { xs: 2, sm: 0 },
          scrollPaddingLeft: { xs: 16, sm: 0 },
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
        }}
      >
        {shownColumns.map((column) => {
          const isTarget = dropTarget === column.status && dragged?.status !== column.status;
          const label = STATUS_LABEL[column.status] || column.label;
          return (
            <Box
              key={column.status}
              role="region"
              aria-label={`${label}: ${column.total}`}
              onDragOver={(e) => {
                if (!dragged) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropTarget(column.status);
              }}
              onDragLeave={() => setDropTarget((prev) => (prev === column.status ? null : prev))}
              onDrop={(e) => { e.preventDefault(); handleDrop(column.status); }}
              sx={{
                width: COLUMN_WIDTH,
                flexShrink: 0,
                scrollSnapAlign: 'start',
                bgcolor: isTarget ? tokens.plumSoft : tokens.surfaceStrong,
                border: '1px solid',
                borderColor: isTarget ? tokens.plumLight : 'transparent',
                borderRadius: '14px',
                p: 1.25,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: { xs: 'none', md: 'calc(100vh - 290px)' },
                minHeight: { md: 280 },
                transition: 'background-color .15s ease, border-color .15s ease',
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} sx={{ px: 0.75, pt: 0.25, pb: 1.25 }}>
                <StatusDot status={column.status} />
                <Typography component="h2" noWrap sx={{ fontSize: '0.9375rem', fontWeight: 700, flexGrow: 1, minWidth: 0, color: tokens.ink }}>
                  {label}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    minWidth: 26, height: 22, px: 0.75, borderRadius: 999, bgcolor: tokens.paper, color: tokens.inkSoft,
                    fontSize: '0.8125rem', fontWeight: 700, display: 'grid', placeItems: 'center', fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {column.total}
                </Box>
              </Stack>

              <Stack gap={1} sx={{ overflowY: 'auto', flexGrow: 1, minHeight: 64, mx: -0.5, px: 0.5, pb: 0.5 }}>
                {column.tasks.length === 0 ? (
                  <Box sx={{
                    py: 3, px: 1, textAlign: 'center', border: '1px dashed', borderColor: isTarget ? tokens.plumLight : tokens.lineStrong,
                    borderRadius: '12px', color: isTarget ? tokens.plum : tokens.muted, fontSize: '0.8125rem', fontWeight: 500,
                  }}>
                    {isTarget ? 'Upuść tutaj' : 'Brak zadań'}
                  </Box>
                ) : (
                  column.tasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onOpen={onOpenTask}
                      onMenu={onCardMenu}
                      draggable={canDrag && !isMobile}
                      onDragStart={handleDragStart}
                      onDragEnd={() => { setDragged(null); setDropTarget(null); }}
                      isDragging={dragged?.id === task.id}
                    />
                  ))
                )}
                {column.total > column.tasks.length && (
                  <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, textAlign: 'center', py: 1 }}>
                    … i {column.total - column.tasks.length} więcej — użyj widoku listy
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TaskBoard;
